import {
  Component,
  NgZone,
  Injector,
  ApplicationRef,
  ComponentRef,
  createComponent,
  AfterViewInit,
  OnInit,
  Input,
  ChangeDetectionStrategy
} from "@angular/core";
import { DecimalPipe } from "@angular/common";
import { Sensor } from "src/app/models/sensor.model";
import { SensorRecord } from "src/app/models/sensorRecord.model";
import { SensorStatusEnum } from "src/app/enums/status.enum";
import { AuthService } from "src/app/services";
import { UserPreferences } from "src/app/models/userPreferences.model";
import { TemperatureUnitEnum } from "src/app/enums/temperatureUnit.enum";
import { PmUnitEnum } from "src/app/enums/pmUnit.enum";
import { EventService } from "src/app/services/event.service";
import { Router } from "@angular/router";
import { MonitoringSite } from "src/app/models/monitoringSite.model";
import { InfoWindowComponent } from "src/app/components/info-window/info-window.component";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { Loader } from "@googlemaps/js-api-loader";
import { ProgressSpinnerMode } from "@angular/material/progress-spinner";
import { ThemePalette } from "@angular/material/core";
import { getGoogleMapsLoader } from "src/app/_helpers/google-maps-loader-helper";
import { TranslateService } from "@ngx-translate/core";
import { DateFormatService } from "src/app/services/dateFormat.service";
declare var google: any;

@Component({
  standalone: false,
  selector: "app-device-details-map-clusterer",
  templateUrl: "./device-details-map-clusterer.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./device-details-map-clusterer.component.scss",
})
export class DeviceDetailsMapClustererComponent implements OnInit, AfterViewInit {
  @Input() loading = true;
  markerClusterer: MarkerClusterer;
  currentInfoWindow: any;
  preferences: UserPreferences;
  defaultCenter: google.maps.LatLngLiteral;
  compRef: ComponentRef<InfoWindowComponent>;
  scriptLoader!: Loader;
  options!: google.maps.MapOptions;
  nativeMap!: google.maps.Map;
  @Input() spinnerColor?: ThemePalette;
  @Input() spinnerDiameter = 100;
  @Input() spinnerMode: ProgressSpinnerMode = 'indeterminate';
  @Input() spinnerStrokeWidth?: number;
  @Input() spinnerValue?: number;

  constructor(
    private authService: AuthService,
    private dateFormatService: DateFormatService,
    private decimalPipe: DecimalPipe,
    private eventService: EventService,
    private router: Router,
    private zone: NgZone,
    private injector: Injector,
    private appRef: ApplicationRef,
    private translateService: TranslateService,
  ) {
    const currentUser = this.authService.getCurrentUser();
    this.preferences = currentUser.preferences;
  }

  ngOnInit(): void {
    const loader = getGoogleMapsLoader(this.translateService.currentLang);
    this.scriptLoader = loader;
  }

  ngAfterViewInit() {
    this.loadDeviceDetailMap();
    this.loading = false;
  }

  loadDeviceDetailMap() {
    this.scriptLoader.load().then(async (google) => {
      this.options = {
        center: { lat: 0, lng: 0 },
        zoom: 10,
        zoomControl: false,
        scaleControl: true,
        mapId: "4504f8b37365c3d0",
        mapTypeControl: true,
        mapTypeId: "roadmap",
        mapTypeControlOptions: {
          style: google.maps.MapTypeControlStyle.DROPDOWN_MENU,
          mapTypeIds: ["roadmap", "hybrid", "terrain", "satellite"],
          position: google.maps.ControlPosition.LEFT_TOP,
        },
        streetViewControl: false,
        rotateControl: true,
        fullscreenControl: true,
        gestureHandling: "greedy",
      };
      const { Map } = (await google.maps.importLibrary(
        "maps"
      )) as google.maps.MapsLibrary;

      const bounds = new google.maps.LatLngBounds();
      bounds.extend(new google.maps.LatLng({ lat: 0, lng: 0 }));
      this.options.center = bounds.getCenter().toJSON();

      this.nativeMap = new Map(
        document.getElementById("map") as HTMLElement,
        this.options
      );
    });
  }

  ngOnDestroy(): void {
    if (this.compRef) {
      this.compRef.destroy();
    }
  }

  updateMarkerClusterer(
    sensorRecords: SensorRecord[],
    sensors: Sensor[],
    isFilteredByDate: boolean = false,
    monitoringSites: MonitoringSite[] = []
  ): void {
    const filteredMarkers = this.filterSensorRecords(
      sensorRecords,
      sensors,
      isFilteredByDate
    );
    const markers: any[] = [];
    const bounds = new google.maps.LatLngBounds();
    const temperatureUnit =
      this.preferences.temperatureUnitId === TemperatureUnitEnum.Fahrenheit
        ? "°F"
        : "°C";
    const pmUnit =
      this.preferences.pmUnitId === PmUnitEnum.MgPerM3 ? "mg/m³" : "µg/m³";
    filteredMarkers.forEach((data) => {
      const marker = new google.maps.Marker({
        position: {
          lat: data.lat,
          lng: data.lon,
        },
        icon:
          data.statusId === SensorStatusEnum.Critical
            ? "./assets/images/red_circle.png"
            : data.statusId === SensorStatusEnum.Warning
              ? "./assets/images/orange_circle.png"
              : "./assets/images/green_circle.png",
      });
      bounds.extend(marker.getPosition());
      const infoWindow = new google.maps.InfoWindow({
        content: `<table class="table">
                          <thead>
                              <tr>
                                  <th scope="col">Name</th>
                                  <th scope="col">Value</th>
                              </tr>
                          </thead>
                          <tbody>
                              <tr>
                                  <td>Device Name</td>
                                  <td>${data.deviceName !== undefined ? data.deviceName : "N/A"}</td>
                              </tr>
                              <tr>
                                  <td>Device ID</td>
                                  <td>${data.deviceId}</td>
                              </tr>
                              ${data.endPointName !== null && data.endPointName !== undefined
            ? "<tr><td>End Point Name</td><td>" + data.endPointName + "</td></tr>"
            : ""
          }
                              <tr>
                                  <td>Date&Time</td>
                                  <td>${this.dateFormatService.formatUtcTimestampToUserTimezone(data.timestamp)}</td>
                              </tr>
                              ${"<tr><td>PM1.0 [" + pmUnit + "]</td><td>" + this.decimalPipe.transform(data.pm1, "1.0-2") + "</td></tr>"}
                              ${"<tr><td>PM2.5 [" + pmUnit + "]</td><td>" + this.decimalPipe.transform(data.pm2_5, "1.0-2") + "</td></tr>"}
                              ${"<tr><td>PM10.0 [" + pmUnit + "]</td><td>" + this.decimalPipe.transform(data.pm10, "1.0-2") + "</td></tr>"}
                              ${"<tr><td>Respirable PM [" + pmUnit + "]</td><td>" + this.decimalPipe.transform(data.respirablePm, "1.0-2") + "</td></tr>"}
                              <tr>
                                  <td>Latitude [°]</td>
                                  <td>${data.lat}</td>
                              </tr>
                              <tr>
                                  <td>Longitude [°]</td>
                                  <td>${data.lon}</td>
                              </tr>
                              <tr>
                                  <td>RSSI</td>
                                  <td>${data.rssi}</td>
                              </tr>
                              ${"<tr><td>Temperature [" + temperatureUnit + "]</td><td>" + this.decimalPipe.transform(data.tempC, "1.0-2") + "</td></tr>" +
          "<tr><td>Humidity [%]</td><td>" + this.decimalPipe.transform(data.hum, "1.0-2") + "</td></tr>"}
                              ${"<tr><td>TVOC [PPM]</td><td>" + this.decimalPipe.transform(data.tvoc, "1.0-2") + "</td></tr>"}
                              ${"<tr><td>CO2 [PPM]</td><td>" + this.decimalPipe.transform(data.cO2, "1.0-2") + "</td></tr>"}
                          </tbody>
                      </table>`,
      });
      marker.addListener("click", () => {
        if (
          this.currentInfoWindow !== null &&
          this.currentInfoWindow !== undefined
        ) {
          this.currentInfoWindow.close();
        }
        if (this.compRef) {
          this.compRef.destroy();
        }
        infoWindow.open(this.nativeMap, marker);
        this.currentInfoWindow = infoWindow;
        this.eventService.broadcast("markerClick", null);
      });
      markers.push(marker);
    });

    monitoringSites.forEach((site) => {
      const marker = new google.maps.Marker({
        position: {
          lat: site.latitude,
          lng: site.longitude,
        },
        icon: "./assets/images/green_triangle.png",
      });
      const infoWindow = new google.maps.InfoWindow();

      marker.addListener("click", (e) => {
        this.zone.run(() => this.onMarkerClick(marker, site, infoWindow, e));
        this.eventService.broadcast("markerClick", null);
      });
      infoWindow.addListener("closeclick", (_) => {
        this.compRef.destroy();
      });
      markers.push(marker);
    });

    if (filteredMarkers.length > 0) {
      this.nativeMap.fitBounds(bounds);
      if (this.nativeMap.getZoom() > 19) {
        this.nativeMap.setZoom(19);
      }
    }

    if (!this.markerClusterer) {
      this.loading = false;
      this.markerClusterer = new MarkerClusterer({
        markers: markers,
        map: this.nativeMap /* , renderer: renderer */,
      });
    } else {
      this.markerClusterer.clearMarkers();
      this.markerClusterer.addMarkers(markers, false);
    }

  }

  onMarkerClick(marker, site: MonitoringSite, infoWindow, e) {
    if (
      this.currentInfoWindow !== null &&
      this.currentInfoWindow !== undefined
    ) {
      this.currentInfoWindow.close();
    }
    if (this.compRef) {
      this.compRef.destroy();
    }

    this.compRef = createComponent(InfoWindowComponent, {
      environmentInjector: this.appRef.injector,
      elementInjector: this.injector,
    });

    this.compRef.instance.siteId = site.id;
    this.compRef.instance.latitude = site.latitude;
    this.compRef.instance.longitude = site.longitude;

    this.appRef.attachView(this.compRef.hostView);

    const div = document.createElement("div");
    div.appendChild(this.compRef.location.nativeElement);

    infoWindow.setContent(div);
    infoWindow.open(this.nativeMap, marker);
    this.currentInfoWindow = infoWindow;
  }

  private filterSensorRecords(
    sensorRecords: SensorRecord[],
    sensors: Sensor[],
    isFilteredByDate: boolean
  ) {
    const filteredMarkers = sensorRecords.filter(
      (marker) =>
        (marker.lat !== 0 || marker.lon !== 0) &&
        (marker.hdop === undefined || marker.hdop < 20) &&
        marker.lon >= -180 &&
        marker.lon <= 180 &&
        marker.lat >= -90 &&
        marker.lat <= 90 &&
        (!isFilteredByDate || marker.statusId !== SensorStatusEnum.Offline)
    );

    const sensorDict: { [key: string]: string | undefined } = {};
    sensors.forEach((sensor) => (sensorDict[sensor.id] = sensor.name));

    const markerMap = new Map<string, SensorRecord>();
    filteredMarkers.forEach((marker) => {
      marker.deviceName = sensorDict[marker.deviceId] || marker.deviceId;
      markerMap.set(`${marker.lon}:${marker.lat}`, marker);
    });

    return Array.from(markerMap.values());
  }

}
